import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  Icon,
  LoadingState,
  StatePanel,
  V2Alert,
  V2Badge,
  V2Button,
  V2Card,
  V2DataGrid,
  V2EmptyState,
  V2Tabs,
  type V2BadgeTone,
  type V2TableColumn,
} from '@sakhtar/design-system/react';
import { systemClock } from '@sakhtar/calendar';
import { UserAvatar } from '../../lib/avatars.js';
import { useCapabilities } from '../../lib/capabilities.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { faDigits } from '../../lib/faDigits.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type {
  CommissionRule,
  CommissionSettings,
  PeriodMetrics,
  PlanCandidatesResponse,
  TeamMember,
  TeamPerformanceResponse,
} from '../../lib/types/commission.js';
import { useApiGet } from '../../lib/useApiGet.js';
import { CommissionPlanModal } from './CommissionPlanModal.js';
import { CommissionRulesPanel } from './CommissionRulesPanel.js';
import {
  METHOD_LABELS,
  METRICS,
  PERIOD_PRESETS,
  barPercent,
  growthBp,
  metricById,
  periodFor,
  rankMembers,
  rankMovement,
  sparkPoints,
  targetProgressBp,
  type MetricDef,
  type PeriodPreset,
  type Ranked,
} from './teamPerformanceModel.js';
import './teamPerformance.css';

const fa = (n: number | bigint) => faDigits(n.toString());
const MEDALS = ['🥇', '🥈', '🥉'] as const;

const ZERO: PeriodMetrics = {
  salesRial: '0',
  invoiceCount: 0,
  collectedRial: '0',
  ordersCount: 0,
  quotesCount: 0,
  quotesAccepted: 0,
  leadsCreated: 0,
  leadsWon: 0,
  leadsLost: 0,
  wonRial: '0',
  activitiesDone: 0,
  commissionRial: '0',
  commissionReleasedRial: '0',
};

function percentText(bp: bigint): string {
  const whole = bp / 100n;
  const frac = bp % 100n;
  return `${fa(whole)}${frac >= 10n ? `٫${fa(frac / 10n)}` : ''}٪`;
}

/**
 * «عملکرد تیم فروش» — every salesperson side by side for a period, ranked on a
 * metric you choose, against the period right before it, so who is rising and
 * who is chasing whom is visible at a glance. Commission is managed per person
 * from here (a modal), not in one global list; the account choices that used
 * to sit on this screen live in finance.
 *
 * UI_REFERENCE_SOURCE: Sakhtar Cloud ERP v2.dc.html — `/t/kimia/sales` hub
 *   cards and the V2 card/table language. No dedicated frame exists for a
 *   team leaderboard. status: KIT_CONFORMANT, V2-only; all figures are server
 *   aggregates (integer Rial, exact), none are computed from sampled rows.
 */
export function TeamPerformancePage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { hasCapability } = useCapabilities();
  const { unit, label: unitLabel } = useAmountDisplay();
  const [params, setParams] = useSearchParams();
  const canTeam = hasCapability('sales.team.read');
  const canRules = hasCapability('sales.commission-rule.read');
  const canManage = hasCapability('sales.commission-rule.manage');
  const tab = params.get('tab') === 'products' && canRules ? 'products' : 'team';
  const preset = (params.get('period') as PeriodPreset | null) ?? 'thisMonth';
  const metricId = (params.get('metric') as MetricDef['id'] | null) ?? 'sales';
  const metric = metricById(metricId);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [ruleQuery, setRuleQuery] = useState('');

  const range = useMemo(() => periodFor(preset, systemClock.today()), [preset]);
  const team = useApiGet<TeamPerformanceResponse>(
    slug && canTeam && tab === 'team'
      ? `${base}/api/v1/sales/commission/team?from=${range.from}&to=${range.to}`
      : null,
    [slug, range.from, range.to, refreshKey, canTeam, tab],
  );
  const candidates = useApiGet<PlanCandidatesResponse>(
    slug && canRules && tab === 'team' ? `${base}/api/v1/sales/commission/plan-candidates` : null,
    [slug, refreshKey, canRules, tab],
  );
  const settings = useApiGet<CommissionSettings>(
    slug && canRules && tab === 'products' ? `${base}/api/v1/sales/commission/settings` : null,
    [slug, refreshKey, tab],
  );
  const rules = useApiGet<{ items: readonly CommissionRule[] }>(
    slug && canRules && tab === 'products' ? `${base}/api/v1/sales/commission/rules` : null,
    [slug, refreshKey, tab],
  );

  const ranked = useMemo<readonly Ranked[]>(
    () => (team.status === 'ready' ? rankMembers(team.data.members, metric) : []),
    [team, metric],
  );
  const previousRanks = useMemo(() => {
    if (team.status !== 'ready') return new Map<string, number | null>();
    return new Map(
      rankMembers(team.data.members, metric, (m) => m.previous).map((r) => [
        r.member.salespersonId,
        r.rank,
      ]),
    );
  }, [team, metric]);

  if (!slug) return null;
  if (!canTeam && !canRules) {
    return (
      <StatePanel
        variant="permission_denied"
        title="عملکرد تیم فقط برای مدیر فروش و مدیر سازمان است"
      />
    );
  }

  const update = (next: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(next)) {
          if (v === null) p.delete(k);
          else p.set(k, v);
        }
        return p;
      },
      { replace: true },
    );
  const refresh = () => setRefreshKey((k) => k + 1);

  const tabs = [
    ...(canTeam ? [{ id: 'team', label: 'رقابت و عملکرد' }] : []),
    ...(canRules ? [{ id: 'products', label: 'نرخ پورسانت محصولات' }] : []),
  ];

  const money = (digits: string) => `${formatRialFa(digits, unit)} ${unitLabel}`;
  const valueText = (value: bigint | null): string => {
    if (value === null) return '—';
    if (metric.kind === 'money') return money(value.toString());
    if (metric.kind === 'percent') return percentText(value);
    return fa(value);
  };

  const editingMember: TeamMember | null =
    editing && team.status === 'ready'
      ? (team.data.members.find((m) => m.salespersonId === editing) ??
        (() => {
          const c =
            candidates.status === 'ready'
              ? candidates.data.items.find((i) => i.salespersonId === editing)
              : undefined;
          return c
            ? ({
                salespersonId: c.salespersonId,
                name: c.name,
                avatarPreset: c.avatarPreset,
                active: true,
                current: ZERO,
                previous: ZERO,
                pipelineOpenCount: 0,
                pipelineRial: '0',
                pipelineWeightedRial: '0',
                trend: [],
                plan: null,
                targetRial: null,
                monthlyTargetRial: null,
              } satisfies TeamMember)
            : null;
        })())
      : null;

  return (
    <div className="team">
      <V2Tabs
        activeId={tab}
        items={tabs}
        onChange={(id) => update({ tab: id === 'team' ? null : id })}
      />

      {tab === 'products' ? (
        settings.status === 'loading' || rules.status === 'loading' ? (
          <LoadingState />
        ) : settings.status === 'error' || rules.status === 'error' ? (
          <StatePanel variant="permission_denied" title="نرخ پورسانت محصولات قابل نمایش نیست" />
        ) : (
          <>
            {!settings.data.configured ? (
              <V2Alert tone="warn">
                حساب‌های سند پورسانت هنوز در مالی تنظیم نشده است.{' '}
                <Link to={`${base}/finance/sales-accounts`}>تنظیم در مالی</Link>
              </V2Alert>
            ) : null}
            <CommissionRulesPanel
              base={base}
              rules={rules.data.items}
              configured={settings.data.configured}
              query={ruleQuery}
              onQuery={setRuleQuery}
              onSaved={refresh}
            />
          </>
        )
      ) : (
        <>
          <div className="team-controls">
            <div className="team-chips" role="group" aria-label="دوره">
              {PERIOD_PRESETS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className="team-chip"
                  aria-pressed={preset === p.id}
                  onClick={() => update({ period: p.id === 'thisMonth' ? null : p.id })}
                >
                  {p.label}
                </button>
              ))}
            </div>
            {team.status === 'ready' ? (
              <span className="team-controls__range">
                {displayJalaliDate(team.data.period.from)} تا{' '}
                {displayJalaliDate(team.data.period.to)} · در برابر{' '}
                {displayJalaliDate(team.data.previousPeriod.from)} تا{' '}
                {displayJalaliDate(team.data.previousPeriod.to)}
              </span>
            ) : null}
          </div>

          {team.status === 'loading' ? <LoadingState /> : null}
          {team.status === 'error' ? (
            <StatePanel
              variant={team.error.status === 403 ? 'permission_denied' : 'network_error'}
              title={
                team.error.status === 403
                  ? 'دسترسی به عملکرد تیم ندارید'
                  : 'عملکرد تیم قابل نمایش نیست'
              }
            />
          ) : null}

          {team.status === 'ready' ? (
            <>
              {!team.data.accountsConfigured ? (
                <V2Alert tone="warn">
                  حساب‌های سند پورسانت در مالی تنظیم نشده است، پس طرح‌های پورسانت فعال نمی‌شوند.{' '}
                  <Link to={`${base}/finance/sales-accounts`}>تنظیم در مالی</Link>
                </V2Alert>
              ) : null}
              <TeamTotals totals={team.data.totals} money={money} />

              <div className="team-metrics" role="tablist" aria-label="معیار رقابت">
                {METRICS.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    role="tab"
                    aria-selected={metric.id === m.id}
                    title={m.hint}
                    className="team-metric"
                    onClick={() => update({ metric: m.id === 'sales' ? null : m.id })}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {ranked.filter((r) => r.rank !== null).length === 0 ? (
                <V2EmptyState
                  icon="users"
                  title="در این دوره هنوز نتیجه‌ای برای مقایسه نیست"
                  description="با قطعی شدن فاکتورها و بسته شدن سرنخ‌ها، رقابت اینجا نمایش داده می‌شود."
                />
              ) : (
                <>
                  <Podium ranked={ranked} metric={metric} valueText={valueText} />
                  <Leaderboard
                    ranked={ranked}
                    metric={metric}
                    previousRanks={previousRanks}
                    valueText={valueText}
                    money={money}
                    plans={team.data.plans}
                    canManage={canRules}
                    onManage={setEditing}
                  />
                </>
              )}

              <Comparison
                members={ranked.map((r) => r.member)}
                money={money}
                onManage={canRules ? setEditing : undefined}
              />

              {canRules && candidates.status === 'ready' ? (
                <V2Card>
                  <div className="team-add">
                    <span>
                      <b>طرح پورسانت برای عضو دیگر</b>
                      <small>
                        اعضایی که هنوز فروشی ندارند هم می‌توانند از همین حالا طرح داشته باشند.
                      </small>
                    </span>
                    <select
                      className="sk-select"
                      aria-label="انتخاب عضو"
                      value=""
                      onChange={(e) => e.target.value && setEditing(e.target.value)}
                    >
                      <option value="">— انتخاب عضو —</option>
                      {candidates.data.items.map((c) => (
                        <option key={c.salespersonId} value={c.salespersonId}>
                          {c.name}
                          {c.hasPlan ? ' (دارای طرح)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </V2Card>
              ) : null}

              {editingMember ? (
                <CommissionPlanModal
                  base={base}
                  member={editingMember}
                  plan={team.data.plans[editingMember.salespersonId] ?? null}
                  accountsConfigured={team.data.accountsConfigured}
                  canManage={canManage}
                  period={editingMember.current}
                  onClose={() => setEditing(null)}
                  onSaved={() => {
                    setEditing(null);
                    refresh();
                  }}
                />
              ) : null}
            </>
          ) : null}
        </>
      )}
    </div>
  );
}

function Delta({ current, previous }: { readonly current: bigint; readonly previous: bigint }) {
  const bp = growthBp(current, previous);
  if (bp === null)
    return <span className="team-delta team-delta--new">{current > 0n ? 'جدید' : '—'}</span>;
  const up = bp > 0n;
  const flat = bp === 0n;
  return (
    <span className={`team-delta ${flat ? '' : up ? 'team-delta--up' : 'team-delta--down'}`}>
      {flat ? '—' : up ? '▲' : '▼'} {percentText(up ? bp : -bp)}
    </span>
  );
}

function TeamTotals({
  totals,
  money,
}: {
  readonly totals: TeamPerformanceResponse['totals'];
  readonly money: (digits: string) => string;
}) {
  const c = totals.current;
  const p = totals.previous;
  const winBp = (m: PeriodMetrics) => {
    const closed = m.leadsWon + m.leadsLost;
    return closed === 0 ? null : (BigInt(m.leadsWon) * 10_000n) / BigInt(closed);
  };
  const win = winBp(c);
  return (
    <div className="team-kpis">
      <div className="team-kpi">
        <span>فروش تیم</span>
        <b className="sk-num">{money(c.salesRial)}</b>
        <Delta current={BigInt(c.salesRial)} previous={BigInt(p.salesRial)} />
      </div>
      <div className="team-kpi">
        <span>فاکتور قطعی</span>
        <b className="sk-num">{fa(c.invoiceCount)}</b>
        <Delta current={BigInt(c.invoiceCount)} previous={BigInt(p.invoiceCount)} />
      </div>
      <div className="team-kpi">
        <span>پورسانت</span>
        <b className="sk-num">{money(c.commissionRial)}</b>
        <small>آزادشده: {money(c.commissionReleasedRial)}</small>
      </div>
      <div className="team-kpi">
        <span>نرخ برد تیم</span>
        <b className="sk-num">{win === null ? '—' : percentText(win)}</b>
        <small>
          {fa(c.leadsWon)} برنده از {fa(c.leadsWon + c.leadsLost)} سرنخ بسته‌شده
        </small>
      </div>
    </div>
  );
}

function Person({ member, size = 40 }: { readonly member: TeamMember; readonly size?: number }) {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  return (
    <span className="team-person">
      <UserAvatar
        name={member.name}
        source={{ avatarPreset: (member.avatarPreset as never) ?? null, avatarPhotoDataUrl: null }}
        size={size}
      />
      <span>
        <Link className="team-person__link" to={`${base}/crm/team/${member.salespersonId}`}>
          <b>{member.name}</b>
        </Link>
        {member.active ? null : <small>غیرفعال</small>}
      </span>
    </span>
  );
}

function Podium({
  ranked,
  metric,
  valueText,
}: {
  readonly ranked: readonly Ranked[];
  readonly metric: MetricDef;
  readonly valueText: (v: bigint | null) => string;
}) {
  const top = ranked.filter((r) => r.rank !== null && r.rank <= 3).slice(0, 3);
  // Visual order: 2nd, 1st, 3rd — the winner stands in the middle and tallest.
  const order = [
    top.find((r) => r.rank === 2),
    top.find((r) => r.rank === 1),
    top.find((r) => r.rank === 3),
  ];
  return (
    <section className="team-podium" aria-label={`برترین‌ها در ${metric.label}`}>
      {order.map((row, slot) =>
        row ? (
          <div
            key={row.member.salespersonId}
            className={`team-podium__slot team-podium__slot--${row.rank}`}
          >
            <span className="team-podium__medal" aria-hidden>
              {MEDALS[(row.rank ?? 1) - 1]}
            </span>
            <Person member={row.member} size={row.rank === 1 ? 56 : 44} />
            <b className="team-podium__value sk-num">{valueText(row.value)}</b>
            <span className="team-podium__rank">رتبهٔ {fa(row.rank ?? 0)}</span>
          </div>
        ) : (
          <div
            key={`empty-${slot}`}
            className="team-podium__slot team-podium__slot--empty"
            aria-hidden
          />
        ),
      )}
    </section>
  );
}

function Spark({ values }: { readonly values: readonly string[] }) {
  const points = sparkPoints(values);
  if (points.length < 2 || points.every((p) => p === 0)) {
    return <span className="team-spark team-spark--empty" aria-hidden />;
  }
  const step = 100 / (points.length - 1);
  const path = points
    .map((y, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(100 - y).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      className="team-spark"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      role="img"
      aria-label="روند فروش در دوره"
    >
      <path
        d={path}
        fill="none"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinejoin="round"
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

function planSummary(member: TeamMember, plans: TeamPerformanceResponse['plans']): string {
  const plan = plans[member.salespersonId];
  if (!plan) return 'نرخ هر محصول';
  if (plan.status === 'inactive') return 'طرح غیرفعال';
  if (plan.method === 'percent_of_sale') return `${faDigits(plan.ratePercent ?? '')}٪ از فروش`;
  if (plan.method === 'tiered') return `پلکانی (${fa(plan.tiers?.length ?? 0)} پله)`;
  return METHOD_LABELS[plan.method];
}

function Leaderboard({
  ranked,
  metric,
  previousRanks,
  valueText,
  money,
  plans,
  canManage,
  onManage,
}: {
  readonly ranked: readonly Ranked[];
  readonly metric: MetricDef;
  readonly previousRanks: ReadonlyMap<string, number | null>;
  readonly valueText: (v: bigint | null) => string;
  readonly money: (digits: string) => string;
  readonly plans: TeamPerformanceResponse['plans'];
  readonly canManage: boolean;
  readonly onManage: (id: string) => void;
}) {
  const leader = ranked[0]?.value ?? null;
  return (
    <V2Card clipped>
      <ol className="team-board">
        {ranked.map((row, index) => {
          const { member } = row;
          const move = rankMovement(row.rank, previousRanks.get(member.salespersonId) ?? null);
          const above = index > 0 ? ranked[index - 1] : undefined;
          const gap =
            metric.kind === 'money' &&
            above?.value != null &&
            row.value != null &&
            row.rank !== null &&
            above.rank !== null &&
            above.rank < row.rank
              ? above.value - row.value
              : null;
          const target = targetProgressBp(member.current.salesRial, member.targetRial);
          const tone: V2BadgeTone =
            target === null
              ? 'neutral'
              : target >= 10_000n
                ? 'success'
                : target >= 5_000n
                  ? 'info'
                  : 'warn';
          return (
            <li
              key={member.salespersonId}
              className={`team-board__row${row.rank === 1 ? ' is-leader' : ''}`}
            >
              <span className="team-board__rank">
                {row.rank === null ? '—' : fa(row.rank)}
                {move !== null && move !== 0 ? (
                  <small className={move > 0 ? 'is-up' : 'is-down'}>
                    {move > 0 ? '▲' : '▼'}
                    {fa(Math.abs(move))}
                  </small>
                ) : null}
              </span>
              <Person member={member} />
              <div className="team-board__bar">
                <div className="team-board__track">
                  <span style={{ width: `${barPercent(row.value, leader)}%` }} />
                </div>
                <span className="team-board__value sk-num">{valueText(row.value)}</span>
                {metric.id === 'sales' ||
                metric.id === 'commission' ||
                metric.id === 'won' ||
                metric.id === 'activities' ? (
                  <Delta current={row.value ?? 0n} previous={metric.value(member.previous) ?? 0n} />
                ) : null}
              </div>
              <Spark values={member.trend} />
              <div className="team-board__side">
                {target !== null ? (
                  <V2Badge tone={tone}>هدف: {percentText(target)}</V2Badge>
                ) : (
                  <small className="team-board__muted">بدون هدف ماهانه</small>
                )}
                {gap !== null && gap > 0n ? (
                  <small className="team-board__muted">
                    تا نفر بالاتر: {money(gap.toString())}
                  </small>
                ) : null}
                {row.rank === 1 ? <small className="team-board__lead">پیشتاز 🔥</small> : null}
              </div>
              <div className="team-board__plan">
                <small>{planSummary(member, plans)}</small>
                {canManage ? (
                  <V2Button
                    size="sm"
                    variant="secondary"
                    onClick={() => onManage(member.salespersonId)}
                  >
                    مدیریت پورسانت
                  </V2Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </V2Card>
  );
}

type Row = TeamMember & { readonly id: string };

function Comparison({
  members,
  money,
  onManage,
}: {
  readonly members: readonly TeamMember[];
  readonly money: (digits: string) => string;
  readonly onManage: ((id: string) => void) | undefined;
}) {
  const columns: V2TableColumn<Row>[] = [
    { key: 'name', header: 'کارشناس', render: (r) => <Person member={r} size={28} /> },
    {
      key: 'sales',
      header: 'فروش',
      align: 'end',
      render: (r) => <span className="sk-num">{money(r.current.salesRial)}</span>,
    },
    {
      key: 'inv',
      header: 'فاکتور',
      align: 'end',
      render: (r) => <span className="sk-num">{fa(r.current.invoiceCount)}</span>,
    },
    {
      key: 'collected',
      header: 'وصول',
      align: 'end',
      render: (r) => {
        const sales = BigInt(r.current.salesRial);
        return (
          <span className="sk-num">
            {sales > 0n ? percentText((BigInt(r.current.collectedRial) * 10_000n) / sales) : '—'}
          </span>
        );
      },
    },
    {
      key: 'quotes',
      header: 'پیش‌فاکتور (پذیرفته)',
      align: 'end',
      render: (r) => (
        <span className="sk-num">
          {fa(r.current.quotesCount)} ({fa(r.current.quotesAccepted)})
        </span>
      ),
    },
    {
      key: 'leads',
      header: 'سرنخ جدید',
      align: 'end',
      render: (r) => <span className="sk-num">{fa(r.current.leadsCreated)}</span>,
    },
    {
      key: 'win',
      header: 'برد / باخت',
      align: 'end',
      render: (r) => (
        <span className="sk-num">
          {fa(r.current.leadsWon)} / {fa(r.current.leadsLost)}
        </span>
      ),
    },
    {
      key: 'pipeline',
      header: 'قیف باز (وزنی)',
      align: 'end',
      render: (r) => <span className="sk-num">{money(r.pipelineWeightedRial)}</span>,
    },
    {
      key: 'acts',
      header: 'فعالیت',
      align: 'end',
      render: (r) => <span className="sk-num">{fa(r.current.activitiesDone)}</span>,
    },
    {
      key: 'comm',
      header: 'پورسانت',
      align: 'end',
      render: (r) => <span className="sk-num">{money(r.current.commissionRial)}</span>,
    },
    ...(onManage
      ? [
          {
            key: 'manage',
            header: '',
            align: 'end' as const,
            render: (r: Row) => (
              <V2Button size="sm" variant="ghost" onClick={() => onManage(r.salespersonId)}>
                <Icon name="settings" size="sm" /> طرح پورسانت
              </V2Button>
            ),
          },
        ]
      : []),
  ];
  return (
    <V2DataGrid
      icon="users"
      title="مقایسهٔ کامل کارشناسان"
      subtitle="همهٔ شاخص‌های دورهٔ انتخاب‌شده کنار هم"
      columns={columns}
      rows={members.map((m) => ({ ...m, id: m.salespersonId }))}
      rowKey={(r) => r.id}
      zebra
      emptyState={<V2EmptyState icon="users" title="کارشناسی برای مقایسه نیست" />}
    />
  );
}
